import { repeat } from "lodash";

import * as BusbudErrors from "@busbud/busbud-errors";

import { FORM_FIELD_KEY } from "@app/modules/checkout/utils/form-fields.constants";
import type { TranslatorService } from "@app/services/translator";
import type {
  CheckoutFormField,
  CheckoutInputState,
  PaymentInfo
} from "@app/types/checkout-form-types";
import type {
  CartInstallments,
  PaymentCard,
  PaymentInfoValues
} from "@app/types/checkout-types";
import type { CreditCardBrand, InstallmentsPlan } from "@app/types/napi-types";
import type { NapiV2InstallmentsPlan } from "@app/types/napi-v2-purchase-types";

import * as InputValidator from "../../../../helpers/input-validator";
import formatter from "../../../../utils/formatter";
import {
  isInternalInstallments,
  isExternalInstallments,
  isEmptyExternalInstallments
} from "../../installments";
import {
  findOrThrow,
  getInitialInputState,
  updateInputState
} from "../checkout-form";

function _createSpreedlyPaymentInfoFields(
  translator: TranslatorService,
  supported_credit_cards: Array<CreditCardBrand>,
  default_name: string,
  card: PaymentCard | undefined
): Array<CheckoutFormField> {
  const card_cvc_length = card ? card.cvcLength[0] : 3;

  const card_number = {
    key: FORM_FIELD_KEY.CARD_NUMBER,
    // Use "tel" type so mobile keyboard shows numbers only
    type: "tel",
    label: translator.t("!checkout.payment.card-number"),
    placeholder: translator.t("!checkout.payment.card-number.placeholder"),
    autocomplete: "cc-number",
    name: "cardnumber",
    columns: 6,
    formatter: function (value: string) {
      return formatter.cardNumber(value);
    },
    validator: InputValidator.combineValidators([
      InputValidator.required(),
      InputValidator.creditCard(),
      InputValidator.supportedCreditCard(
        supported_credit_cards,
        translator.t("!checkout.payment.error.unsupported-credit-card")
      )
    ])
  };

  const card_expiry = {
    key: FORM_FIELD_KEY.CARD_EXPIRY,
    type: "text",
    label: translator.t("!checkout.payment.expiry-date"),
    autocomplete: "cc-exp",
    name: "cc-exp",
    columns: 6,
    validator: InputValidator.combineValidators([
      InputValidator.required(),
      InputValidator.cardExpiry()
    ])
  };

  const card_holder = {
    key: FORM_FIELD_KEY.CARD_CARDHOLDER,
    type: "text",
    label: translator.t("!checkout.payment.cardholder-name"),
    autocomplete: "cc-name",
    name: "ccname",
    columns: 6,
    validator: InputValidator.combineValidators([
      InputValidator.required(),
      InputValidator.minWords(2)
    ]),
    default: default_name
  };

  const cvc = {
    key: FORM_FIELD_KEY.CARD_CVC,
    type: "tel",
    label: translator.t("!checkout.payment.cvc"),
    placeholder: repeat("•", card_cvc_length),
    autocomplete: "cc-csc",
    name: "cvc",
    columns: 3,
    formatter: function (value: string) {
      return value.replace(/\D/g, "");
    },
    validator: InputValidator.combineValidators([
      InputValidator.required(),
      InputValidator.regExp(new RegExp("^[0-9]{" + card_cvc_length + "}$"))
    ])
  };

  // @ts-expect-error
  return [card_holder, card_number, card_expiry, cvc];
}

function _createInternalInstallments(
  translator: TranslatorService,
  available_quantities: number[]
): CheckoutFormField {
  if (!available_quantities) {
    throw new BusbudErrors.Impossible(
      "internal installment plans must always have available_quantities"
    );
  }

  const options = available_quantities.map(i => ({
    value: i.toString(),
    label: `${i}x`
  }));

  return {
    key: FORM_FIELD_KEY.INSTALLMENTS,
    type: "select",
    label: translator.t("!checkout.payment.installments"),
    columns: 3,
    options,
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    default: options[0].value,
    tooltip: translator.t("!checkout.payment.installments.tooltip")
  };
}

function _createExternalInstallments(
  translator: TranslatorService,
  installments_plans: InstallmentsPlan[] | NapiV2InstallmentsPlan[] | null
): CheckoutFormField {
  let options = [{ value: "1", label: "1x" }];

  if (!!installments_plans && installments_plans.length !== 0) {
    options = installments_plans.map(({ id, installments }) => ({
      value: id,
      label: `${installments}x`
    }));
  }

  // Cart V2 API does not return 1x plan
  const no_1x_plan = !installments_plans?.some(plan => plan.installments === 1);

  if (no_1x_plan) {
    options.unshift({ value: "1", label: "1x" });
  }

  return {
    key: FORM_FIELD_KEY.INSTALLMENTS_PLANS,
    type: "select",
    label: translator.t("!checkout.payment.installments"),
    columns: 3,
    options,
    disabled: !installments_plans || installments_plans.length === 0,
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    default: options[0].value,
    tooltip: translator.t("!checkout.payment.installments.tooltip")
  };
}

export function createPaymentInfoFields(params: {
  card: PaymentCard | undefined;
  installments?: CartInstallments;
  installments_plans: InstallmentsPlan[] | NapiV2InstallmentsPlan[] | null;
  supported_credit_cards: CreditCardBrand[];
  translator: TranslatorService;
  default_name?: string;
}): Array<CheckoutFormField> {
  const {
    card,
    default_name = "",
    installments,
    installments_plans = [],
    supported_credit_cards,
    translator
  } = params;

  const fields = _createSpreedlyPaymentInfoFields(
    translator,
    supported_credit_cards,
    default_name,
    card
  );

  // TODO-CART-V2-CLEAN: cart v2 don't have internal installments
  if (isInternalInstallments(installments)) {
    fields.push(
      _createInternalInstallments(translator, installments.available_quantities)
    );
  }

  if (
    (installments_plans && installments_plans.length > 0) ||
    isExternalInstallments(installments) ||
    isEmptyExternalInstallments(installments)
  ) {
    fields.push(_createExternalInstallments(translator, installments_plans));
  }

  return fields;
}

export function createPaymentInfoValues(
  payment_info_fields: Array<CheckoutFormField>,
  values: Record<string, CheckoutInputState>
): Record<string, CheckoutInputState> {
  return payment_info_fields.reduce(
    (payment_info_values, field) => ({
      ...payment_info_values,
      [field.key]: getInitialInputState(
        field,
        // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        (values && values[field.key] && values[field.key].value) ||
          field.default
      )
    }),
    {}
  );
}

export function updatePaymentInfoFields(params: {
  translator: TranslatorService;
  payment_info_fields: Array<CheckoutFormField>;
  field: CheckoutFormField;
  value: string;
  installments?: CartInstallments;
  installments_plans?: InstallmentsPlan[] | NapiV2InstallmentsPlan[] | null;
  supported_credit_cards: CreditCardBrand[];
  default_name?: string;
}): Array<CheckoutFormField> {
  const {
    translator,
    payment_info_fields,
    field,
    value,
    installments,
    installments_plans = [],
    supported_credit_cards
  } = params;

  if (field.key === FORM_FIELD_KEY.CARD_NUMBER) {
    const card = formatter.getCardFromNumber(value);
    return createPaymentInfoFields({
      translator,
      card,
      installments,
      installments_plans,
      supported_credit_cards
    });
  }

  return payment_info_fields;
}

export function updatePaymentInfoValues(
  payment_info: PaymentInfo,
  field: CheckoutFormField,
  value: string
): Record<string, CheckoutInputState> {
  const input_state = payment_info.values[field.key];

  if (field.key === FORM_FIELD_KEY.CARD_NUMBER) {
    const cvc_field: CheckoutFormField = findOrThrow(payment_info.fields, {
      key: "card_cvc"
    });
    const cvc_state = payment_info.values.card_cvc;
    return Object.assign({}, payment_info.values, {
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      card_number: updateInputState(field, input_state, value),
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      card_cvc: updateInputState(cvc_field, cvc_state, cvc_state.value)
    });
  }

  return Object.assign({}, payment_info.values, {
    // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    [field.key]: updateInputState(field, input_state, value)
  });
}

export function getUpdatedPaymentInfo(
  payment_info_state: Record<string, CheckoutInputState>
): PaymentInfoValues {
  const payment_info: Record<string, string> = {
    card_number: "",
    card_expiry: "",
    card_cardholder: "",
    card_cvc: ""
  };

  Object.keys(payment_info_state).forEach(key => {
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    payment_info[key] = payment_info_state[key].value;
  });

  return payment_info as unknown as PaymentInfoValues;
}

export function getPaymentInfo(params: {
  translator: TranslatorService;
  payment_info?: PaymentInfo;
  installments?: CartInstallments;
  installments_plans?: InstallmentsPlan[] | null;
  supported_credit_cards: CreditCardBrand[];
  default_name?: string;
}): PaymentInfo {
  const {
    translator,
    payment_info,
    installments,
    installments_plans = [],
    supported_credit_cards,
    default_name
  } = params;

  const card =
    payment_info &&
    payment_info.values[FORM_FIELD_KEY.CARD_NUMBER] &&
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    payment_info.values[FORM_FIELD_KEY.CARD_NUMBER].value
      ? formatter.getCardFromNumber(
          // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          payment_info.values[FORM_FIELD_KEY.CARD_NUMBER].value
        )
      : undefined;

  const payment_info_fields = createPaymentInfoFields({
    translator,
    card,
    installments,
    installments_plans,
    supported_credit_cards, // cart.supported_credit_cards["spreedly"],
    default_name
  });

  const payment_info_values = createPaymentInfoValues(
    payment_info_fields,
    (payment_info && payment_info.values) ?? {}
  );

  const installments_field = payment_info_values[FORM_FIELD_KEY.INSTALLMENTS];
  const cart_installments_quantity = installments && installments.quantity;

  // Installments may have been reseted, in which case we need to update the form's input
  if (
    installments_field &&
    installments_field.value !== String(cart_installments_quantity)
  ) {
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    payment_info_values[FORM_FIELD_KEY.INSTALLMENTS].value = String(
      cart_installments_quantity
    );
  }

  const installments_plans_field =
    payment_info_values[FORM_FIELD_KEY.INSTALLMENTS_PLANS];

  let cart_installments_plan_id: string | null = null;

  // TODO-CART-V2-CLEANUP: at the moment we don't have the installment plan id on cart.chargegss
  if (installments && installments_plans) {
    const mapped_installments_plans = installments_plans.find(
      plan => plan.installments === installments.quantity
    );

    cart_installments_plan_id = mapped_installments_plans?.id ?? null;
  } else if (isExternalInstallments(installments)) {
    cart_installments_plan_id = installments.plan_id;
  }

  // Installments plans may have been reseted, in which case we need to update the form's input
  if (
    installments_plans_field &&
    cart_installments_plan_id &&
    installments_plans_field.value !== String(cart_installments_plan_id)
  ) {
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    payment_info_values[FORM_FIELD_KEY.INSTALLMENTS_PLANS].value = String(
      cart_installments_plan_id
    );
  }

  return {
    fields: payment_info_fields,
    values: payment_info_values
  };
}
